import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Microphone, Lightning, ChatCircleDots, Star, ArrowRight } from "@phosphor-icons/react";

const HERO_BG = "https://images.pexels.com/photos/5749191/pexels-photo-5749191.jpeg";

const features = [
  { icon: Microphone, title: "Drop Your Sound", desc: "Upload your mp3 or wav. Land in the automated review queue instantly.", color: "#0038FF" },
  { icon: ChatCircleDots, title: "Live Critique", desc: "Real-time chat and feedback from artists watching the same broadcast.", color: "#B14AED" },
  { icon: Star, title: "Get Rated", desc: "Earn star ratings and written critiques. Climb the leaderboard.", color: "#E1FF00" },
  { icon: Lightning, title: "Skip The Line", desc: "Pay $5 to jump the queue and get priority play during the show.", color: "#E1FF00" },
];

export default function Landing() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const cta = () => (user ? navigate("/show") : login());

  return (
    <div className="min-h-screen bg-[#050505] grain">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/80 to-[#050505]" />
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#B14AED]/20 blur-[120px]" />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#0038FF]/20 blur-[120px]" />
        </div>

        <div className="relative max-w-[1600px] mx-auto px-5 md:px-10 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E1FF00]/40 bg-[#E1FF00]/5 mb-7">
              <span className="w-2 h-2 rounded-full bg-[#FF3B30] animate-pulse" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#E1FF00]">Live Music Review Network</span>
            </div>

            <h1 className="font-head font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-none mb-6">
              Where artists<br />
              <span className="text-[#E1FF00]">push each other</span> forward.
            </h1>

            <p className="text-lg md:text-xl text-[#A1A1AA] max-w-xl mb-9 leading-relaxed">
              Submit your track to the automated queue. Get raw, constructive critique from the room in real time. Or pay <span className="text-[#E1FF00] font-semibold">$5</span> to skip the line and play next.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Button
                data-testid="hero-cta-button"
                onClick={cta}
                className="bg-[#E1FF00] hover:bg-[#CBE600] text-black font-bold rounded-none px-7 py-6 text-base gap-2 hover:-translate-y-1 transition-transform"
              >
                {user ? "Enter The Show" : "Join The Show"} <ArrowRight size={18} weight="bold" />
              </Button>
              <Button
                onClick={() => navigate("/show")}
                variant="ghost"
                className="border border-white/20 text-white hover:bg-white/5 rounded-none px-7 py-6 text-base"
              >
                Watch Live
              </Button>
            </div>
          </motion.div>
        </div>

        <div className="relative border-t border-white/10 bg-black/40">
          <Marquee speed={40} gradient={false} className="py-3">
            {["m1", "m2", "m3", "m4", "m5", "m6"].map((mk) => (
              <span key={mk} className="font-head font-bold text-sm uppercase tracking-wider text-[#52525B] mx-8 flex items-center gap-8">
                Hip-Hop <span className="text-[#E1FF00]">●</span> R&B <span className="text-[#B14AED]">●</span> Trap <span className="text-[#0038FF]">●</span> Pop <span className="text-[#E1FF00]">●</span> Drill <span className="text-[#B14AED]">●</span> Afrobeats
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <h2 className="font-head font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-3">How the show runs</h2>
        <p className="text-[#A1A1AA] text-lg mb-12 max-w-xl">Four moves. One automated stage. The room decides.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="group bg-[#0F0F13] border border-white/5 p-7 hover:-translate-y-1 transition-transform hover:border-white/15"
            >
              <div
                className="w-12 h-12 flex items-center justify-center mb-6 rounded-sm"
                style={{ backgroundColor: `${f.color}1A`, border: `1px solid ${f.color}40` }}
              >
                <f.icon size={24} weight="duotone" style={{ color: f.color }} />
              </div>
              <div className="font-mono text-xs text-[#52525B] mb-2">0{i + 1}</div>
              <h3 className="font-head font-bold text-xl tracking-tight mb-2">{f.title}</h3>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 pb-24">
        <div className="relative overflow-hidden bg-[#050505] border border-[#E1FF00]/40 p-10 md:p-16 text-center live-glow">
          <h2 className="font-head font-black text-3xl md:text-5xl tracking-tighter mb-4">Ready to be heard?</h2>
          <p className="text-[#A1A1AA] text-lg mb-8 max-w-lg mx-auto">The queue is live right now. Drop a track and let the room react.</p>
          <Button
            data-testid="bottom-cta-button"
            onClick={cta}
            className="bg-[#E1FF00] hover:bg-[#CBE600] text-black font-bold rounded-none px-8 py-6 text-base hover:-translate-y-1 transition-transform"
          >
            {user ? "Enter The Show" : "Sign In With Google"}
          </Button>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center">
        <p className="font-mono text-xs text-[#52525B] uppercase tracking-widest">HDN VSN: The Feed Room © 2026</p>
      </footer>
    </div>
  );
}
